import { CanvasElement } from '@/typings/customilyElements/canvasElements';
import { ElementType, ListElements } from '@/typings/customilyElements/layer';

export const isElementColorOption = (element: CanvasElement): boolean => {
    return element.type === 'image-placeholder' && !!element.dynamicImagesPath && element.isColorOptions;
};

export const isElementSolidBackground = (element: CanvasElement): boolean => {
    return element.type === 'vector-placeholder' && !!element.isSolidBackground;
};

export const canChangeElementVisibility = (element: CanvasElement, isPreviewStep: boolean): boolean => {
    // Cannot change visibility for color option elements on step 1
    return !(!isPreviewStep && isElementColorOption(element));
};

export const canChangeElementLock = (element: CanvasElement, isPreviewStep: boolean): boolean => {
    // Cannot change lock for color option elements on step 1
    return !(!isPreviewStep && isElementColorOption(element));
};

export function debounce<A = unknown, R = void>(fn: (args: A) => R, ms: number): (args: A) => Promise<R> {
    let timer = 0;

    const debouncedFunc = (args: A): Promise<R> =>
        new Promise((resolve) => {
            if (timer) {
                clearTimeout(timer);
            }

            timer = setTimeout(() => {
                resolve(fn(args));
            }, ms);
        });
    return debouncedFunc;
}

export const flattenElements = (elements: ListElements[]): ElementType[] => {
    return elements.reduce((prev, item) => {
        return [...prev, item.element, ...flattenElements(item.children || [])];
    }, [] as ElementType[]);
};

export const getImageFromPsdEncodedData = (
    canvas: HTMLCanvasElement,
    encodedData: Uint8ClampedArray,
    width: number,
    height: number,
    resize?: { width: number; height: number }
): string => {
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    const imageData = new ImageData(encodedData, width, height);
    context!.clearRect(0, 0, canvas.width, canvas.height);
    context!.putImageData(imageData, 0, 0);

    if (!resize) {
        const base64 = canvas.toDataURL();
        return base64;
    }

    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = resize.width;
    resizedCanvas.height = resize.height;
    const resizedContext = resizedCanvas.getContext('2d');
    resizedContext!.drawImage(canvas, 0, 0, resize.width, resize.height);
    const base64 = resizedCanvas.toDataURL();
    resizedCanvas.remove();
    return base64;
};

export const isValidCanvasElement = (element: ElementType) => {
    const validElementTypes = new Set([
        'bounded-text',
        'image',
        'image-placeholder',
        'path-itext',
        'vector-placeholder',
        'text-transform'
    ]);
    return validElementTypes.has(element.type);
};
